import axios from 'axios';
import {BASEDOMAIN} from '@/constants/allconstants';
import {AuthConfig} from '@/utils/constants/configContants';
import { PAGE_LENGTH } from '@/utils/constants/constants';

interface TemplateEnvelopeListQuery {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
    filters?: Record<string, any>;
}

interface TemplateEnvelopeListPage<T> {
    data: T[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNextPage: boolean;
    };
}

const mapTemplateEnvelopeFilters = (filters: Record<string, any> = {}) => ({
    createdAtStart: filters.startDate ?? filters.createdAtStart,
    createdAtEnd: filters.endDate ?? filters.createdAtEnd,
    status: filters.status != null && filters.status !== ''
        ? { value: filters.status }
        : undefined,
});

export const fetchTemplates = async (
    pageNo: number,
    dataLength: number,
    sorting: any,
    filter: any,
    searchItem: string,
    callback: (response: any) => void,
    errorCallback: (error: any) => void
) => {
    try {
        const offset = dataLength * pageNo - dataLength;
        const queryParams = new URLSearchParams({
            offset: offset.toString(),
            limit: dataLength.toString(),
            sort: JSON.stringify(sorting),
            admin_portal: "true",
        });

        if (searchItem?.trim()) {
            queryParams.append('search', searchItem.trim());
        }

        if (filter?.createdAtStart) {
            queryParams.append('created_on_or_after', filter.createdAtStart);
        }

        if (filter?.createdAtEnd) {
            queryParams.append('created_on_or_before', filter.createdAtEnd);
        }

        if (filter?.status?.value) {
            queryParams.append('status', filter.status.value.toString());
        }

        if (filter?.owner) {
            queryParams.append('owner', filter.owner);
        }

        const url = `${BASEDOMAIN}/template/list?${queryParams.toString()}`;
        const response = await axios.get(url, AuthConfig());
        callback(response);
    } catch (error) {
        console.error('Error fetching templates:', error);
        errorCallback(error);
    }
};

export const getTemplateById = async (id: string, callback: any, errCallback: any) => {
    await axios.get(`${BASEDOMAIN}/template/${id}?get_content=false`, AuthConfig())
    .then(res => callback(res.data))
    .catch(e => e?.response?.status === 401 ? {} : errCallback(e))
}

export const getDocumentOfTemplate = async (templateId: string, documentId: string) => {
    const res = await axios.get(`${BASEDOMAIN}/template/${templateId}/document/${documentId}`, AuthConfig())
    return res.data
}

export const transferTemplateOwnership = async (templateId: string, payload: { user_id?: string; email_address?: string }) => {
    const response = await axios.post(`${BASEDOMAIN}/template/${templateId}/transfer-ownership`, payload, AuthConfig());
    return response.data;
};

export const fetchTemplateEnvos = async (
    templateId: string,
    pageNo: number,
    dataLength: number,
    sorting: any,
    filter: any,
    searchItem: string,
    callback: (response: any) => void,
    errorCallback: (error: any) => void
) => {
    try {
        const offset = dataLength * pageNo - dataLength;
        const queryParams = new URLSearchParams({
            offset: offset.toString(),
            limit: dataLength.toString(),
            sort: JSON.stringify(sorting),
            admin_portal: "true",
        });

        if (searchItem?.trim()) {
            queryParams.append('search', searchItem.trim());
        }

        if (filter?.createdAtStart) {
            queryParams.append('created_on_or_after', filter.createdAtStart);
        }

        if (filter?.createdAtEnd) {
            queryParams.append('created_on_or_before', filter.createdAtEnd);
        }

        if (filter?.status?.value) {
            queryParams.append('status', filter.status.value.toString());
        }

        const url = `${BASEDOMAIN}/template/${templateId}/envelope?${queryParams.toString()}`;
        const response = await axios.get(url, AuthConfig());
        callback(response);
    } catch (error) {
        console.error('Error fetching template envelopes:', error);
        errorCallback(error);
    }
};

export const getTemplateEnvelopeListPage = <T = any>(
    templateId: string,
    options: TemplateEnvelopeListQuery = {}
): Promise<TemplateEnvelopeListPage<T>> => {
    const page = options.page ?? 1;
    const limit = options.limit ?? PAGE_LENGTH;
    const sortField = options.sort ?? 'created_at';
    const sorting = { [sortField]: options.order === 'asc' ? 1 : -1 };
    const filter = mapTemplateEnvelopeFilters(options.filters);

    return new Promise((resolve, reject) => {
        fetchTemplateEnvos(
            templateId,
            page,
            limit,
            sorting,
            filter,
            options.search ?? '',
            (response: any) => {
                const envelopes = response.data?.envelopes ?? [];
                const total = response.data?.total_count ?? 0;
                const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;

                resolve({
                    data: envelopes,
                    pagination: {
                        page,
                        limit,
                        total,
                        totalPages,
                        hasNextPage: envelopes.length > 0 && page * limit < total,
                    },
                });
            },
            reject
        );
    });
};
